import type { MeProfile } from "@elation/api-client/src/stitch-web";

import { IS_AUTOMATION, IS_CHROME_EXTENSION, IS_IN_IFRAME } from "@src/env";

import { datadogRum } from "./datadog";
import { clearOtelUser, setOtelUser } from "./otel";

function platform(): "browser" | "iframe" | "chrome_extension" | "automation" {
  if (IS_AUTOMATION) return "automation";
  if (IS_CHROME_EXTENSION) return "chrome_extension";
  if (IS_IN_IFRAME) return "iframe";
  return "browser";
}

/**
 * Attaches the authenticated user's identity to the active Datadog RUM session
 * and the OTEL module-level user context. Every subsequent action emitted via
 * the events registry inherits identity attributes from this call, so per-event
 * identity properties are unnecessary.
 *
 * Call from each login completion path immediately after `/me` resolves, and
 * before emitting the first product event in the session. Also called on
 * bootstrap (`ActorProvider`) when an existing session is restored without a
 * fresh login (e.g. a page reload), so identity survives reloads.
 */
export function setUser(profile: MeProfile): void {
  const { user, practice, type } = profile;
  const name = [user.firstName, user.lastName]
    .filter((part): part is string => Boolean(part))
    .join(" ")
    .trim();

  const p = platform();

  datadogRum.setUser({
    id: String(user.id),
    ...(user.email ? { email: user.email } : {}),
    ...(name ? { name } : {}),
    practice_id: practice.id,
    user_type: type,
    platform: p,
  });

  setOtelUser({
    "user.id": String(user.id),
    "user.roles": [type],
    "practice.id": practice.id,
  });
}

/**
 * Clears the active Datadog RUM session identity and OTEL user context. Call
 * from any sign-out path so the next session starts clean.
 */
export function clearUser(): void {
  datadogRum.clearUser();
  clearOtelUser();
}
